import headerStyles from "../../styles/header.module.css";
import HeaderCalendar from "./HeaderCalendar";
import HeaderKPIList from "./HeaderKPIList";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getActiveNexusFilters } from "../../store/nexusFilters/action";
import { useEffect, useState } from "react";
import { ReactComponent as Logo } from "../../assets/Pondoo_clean_logo.svg";
import labels from "../../utils/labels";
import { setCompareWithNexus } from "../../store/KPICollection";
import Switch from "../UI/input/Switch";
import { usePathCheck } from "../../hooks/usePathCheck";
import SendToNexus from "./SendToNexusButton";
import HeaderFilters from "./HeaderFilter";
import { useSearchParams } from "react-router-dom";
import { useSolutionOverviewStatus } from "../../hooks/queryHooks";
import useDataSelector from "../../hooks/useDataSelector";
import { ProblemsAndSolutionsButton } from "./ProblemsAndSolutionsButton";

const Header = () => {
  const dispatch = useAppDispatch();
  const isKpiPage = usePathCheck();
  const dataToUse = useDataSelector();
  const compareWithNexus = useAppSelector(
    (state) => state.kpiCollectionSlice.comepareWithNexus
  );
  const interceptorIsReady = useAppSelector(
    (state) => state.apiConfigurationState.interceptorIsReady
  );

  const [searchParams, _] = useSearchParams();
  const date = searchParams.get("date");
  const filter = searchParams.get("filter");

  const overviewState = useSolutionOverviewStatus({
    filterId: (filter && parseInt(filter)) || undefined,
    date: date || undefined,
  });

  /**
   * Initial call to hydrate planningDate objects - gives a "data readiness" overview for each day
   */
  useEffect(() => {
    if (interceptorIsReady) dispatch(getActiveNexusFilters());
  }, [interceptorIsReady, dispatch]);

  /**
   * Data object to display
   */
  const { selectedGanttChartTab, ganttChartData } = useAppSelector(
    (state) => state.ganttChartSlice
  );

  const isCurrentOrFutureDate = (inputDateString?: string | null): boolean => {
    if (!inputDateString) return false;
    const [year, month, day] = inputDateString.split("-").map(Number);
    const inputDate = new Date(year, month - 1, day);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    return today <= inputDate;
  };

  const [showSendToNexusButton, setShowSendToNexusButton] = useState<boolean>();
  const acceptedDate = isCurrentOrFutureDate(date);

  useEffect(() => {
    setShowSendToNexusButton(
      acceptedDate &&
        dataToUse &&
        (selectedGanttChartTab === "Optimised" ||
          selectedGanttChartTab === "Custom" ||
          (isKpiPage && overviewState.pondooStatus === "ready"))
    );
  }, [dataToUse]); // To make sure data is ready for export, update each time the data object is changed.

  return (
    <div className={headerStyles.header}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-[var(--spacing-lg)]">
          <Logo width={40} height={40} />
          <div className={headerStyles.headerFilterWrapper}>
            <HeaderFilters />

            <HeaderCalendar />
            <HeaderKPIList />
          </div>
        </div>
        <div className="flex items-center gap-[var(--spacing-sm)]">
          <ProblemsAndSolutionsButton />
          {isKpiPage && ganttChartData && (
            <Switch
              label={labels.compareWithNexus}
              id="compare"
              checked={compareWithNexus}
              onChange={(checked) => dispatch(setCompareWithNexus(checked))}
              labelPlacement="BEFORE"
            />
          )}
          {/*
          Enable send to nexus if
          1) we're looking at the Pondoo Plan, or
          2) we're looking at the KPI page and the optimization has run
          3) regardless of previous conditions, only enable if we are on a current or future date
          */}
          {showSendToNexusButton && (
            <SendToNexus
              check={dataToUse?.enableSendPlanToNexus || false}
              idScenario={dataToUse?.idScenario ? dataToUse?.idScenario : -1}
              inEditMode={selectedGanttChartTab === "Custom"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
